// Pure helpers for the clinic/branch API-key management screens.
// No imports: node's test runner loads this file directly.

const MESSAGES = {
  nameRequired: "กรุณาระบุชื่อคีย์",
  scopesRequired: "เลือกข้อมูลอย่างน้อย 1 รายการ",
  branchRequired: "กรุณาเลือกสาขา",
  termsRequired: "ต้องยอมรับข้อตกลง PDPA ก่อนเปิดสิทธิ์อ่านข้อมูลคนไข้",
  invalidIpAllowlist: "รูปแบบ IP ไม่ถูกต้อง ใส่ IP หรือ CIDR บรรทัดละ 1 รายการ",
  notFound: "ไม่พบคีย์นี้ (อาจถูกลบไปแล้ว) กรุณาโหลดหน้าใหม่",
  forbidden: "คุณไม่มีสิทธิ์จัดการ API Key นี้",
  generic: "เกิดข้อผิดพลาด กรุณาลองใหม่",
  unlimited: "ไม่จำกัด"
};

const SCOPE_LABELS = {
  "receipt:read": "ใบเสร็จ การชำระเงิน และค่า DF แพทย์",
  "appointment:read": "นัดหมาย",
  "patient:read": "ข้อมูลคนไข้ (ชื่อ เบอร์โทร ที่อยู่ — ไม่รวมเลขบัตรประชาชนและข้อมูลการรักษา)",
  "schedule:read": "ตารางแพทย์"
};

export const API_KEY_SCOPES = ["receipt:read", "appointment:read", "patient:read", "schedule:read"];
export const PATIENT_SCOPE = "patient:read";
export const ALL_BRANCHES = "ALL";

// A key mutation (create/rotate/delete/toggle) may start only when no plaintext key is
// waiting to be seen and nothing else is in flight, so one response can never overwrite another.
export function canStartKeyMutation({ revealed = null, isSubmitting = false, busyKeyId = null } = {}) {
  return !revealed && !isSubmitting && (busyKeyId === null || busyKeyId === undefined);
}

export function scopeLabel(scope) {
  return SCOPE_LABELS[scope] || scope;
}

export function apiKeyBasePath({ level, clinicUrl, branchUrl } = {}) {
  if (!clinicUrl) throw new Error("clinicUrl is required");
  const c = encodeURIComponent(clinicUrl);
  if (level === "clinic") return `/api/clinicApiKey/${c}`;
  if (level === "branch") {
    if (!branchUrl) throw new Error("branchUrl is required for branch level");
    return `/api/branchApiKey/${c}/${encodeURIComponent(branchUrl)}`;
  }
  throw new Error(`unknown API key level: ${level}`);
}

export function parseIpAllowlist(text) {
  const items = String(text || "")
    .split(/[\s,]+/)
    .filter(Boolean);
  const unique = [...new Set(items)];
  return unique.length ? unique : null;
}

function hasPatientScope(scopes) {
  return Array.isArray(scopes) && scopes.includes(PATIENT_SCOPE);
}

// "ALL" (clinic-wide) or a finite positive integer id; anything else would
// serialise to null and silently create a clinic-wide key.
function isValidBranchChoice(value) {
  if (value === ALL_BRANCHES) return true;
  if (value === "" || value === null || value === undefined) return false;
  if (typeof value === "string" && !value.trim()) return false;
  const n = Number(value);
  return Number.isInteger(n) && n > 0;
}

export function validateApiKeyForm({ level, form }) {
  const errors = {};
  if (!String(form.name || "").trim()) errors.name = MESSAGES.nameRequired;
  if (!Array.isArray(form.scopes) || form.scopes.length === 0) {
    errors.scopes = MESSAGES.scopesRequired;
  }
  if (level === "clinic" && !isValidBranchChoice(form.branchId)) {
    errors.branchId = MESSAGES.branchRequired;
  }
  if (hasPatientScope(form.scopes) && !form.acceptTerms) {
    errors.acceptTerms = MESSAGES.termsRequired;
  }
  return errors;
}

export function buildCreatePayload({ level, form }) {
  const payload = {
    name: String(form.name || "").trim(),
    scopes: API_KEY_SCOPES.filter(s => (form.scopes || []).includes(s)),
    ipAllowlist: parseIpAllowlist(form.ipText)
  };
  if (level === "clinic") {
    payload.branchId = form.branchId === ALL_BRANCHES ? null : Number(form.branchId);
  }
  if (hasPatientScope(payload.scopes)) payload.acceptTerms = true;
  return payload;
}

export function describeApiQuota(usage) {
  if (!usage) return null;
  const used = Number(usage.used) || 0;
  const { limit } = usage;
  const usedText = used.toLocaleString("en-US");
  if (limit === null || limit === undefined) {
    return { period: usage.period, usedText, limitText: MESSAGES.unlimited, percent: null, variant: "primary" };
  }
  const percent = limit === 0 ? 100 : Math.min(100, Math.round((used * 100) / limit));
  let variant = "primary";
  if (limit === 0 || used >= limit) variant = "danger";
  else if (percent >= 80) variant = "warning";
  return { period: usage.period, usedText, limitText: Number(limit).toLocaleString("en-US"), percent, variant };
}

// `notFound: false` is for list loads, where a 404 is a load failure, not a missing key.
export function apiKeyErrorMessage(error, fallback = MESSAGES.generic, { notFound = true } = {}) {
  const response = error && error.response;
  const raw = response && response.data && response.data.errorMessage;
  const text = raw && typeof raw === "object" ? raw.message : raw;
  const code = typeof text === "string" ? text.split(":")[0].trim() : "";
  if (code === "TERMS_REQUIRED") return MESSAGES.termsRequired;
  if (code === "INVALID_IP_ALLOWLIST") return MESSAGES.invalidIpAllowlist;
  const status = response && response.status;
  if (status === 404 && notFound) return MESSAGES.notFound;
  if (status === 403) return MESSAGES.forbidden;
  return fallback;
}

export function isPublicApiEnabledFor(tenant) {
  return tenant !== "tantatat";
}
